/* Copyright (c) 2020-present CloudIO Inc */

import {
  useStateX,
  useStateXAtomSetter,
  useStateXSelector,
} from '@cloudio-saas/statex';
import {
  Box,
  FormControl,
  Input,
  InputAdornment,
  InputLabel,
  Popover,
  Theme,
} from '@mui/material';
import ColorPicker from 'custom/ColorPicker';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { ColorExpressionProp, ExpressionValue } from 'componentregistry';
import { helpTextSelector } from 'designer/state';
import { expressionPropsAtom } from 'expression/builder/state';
import useLocalState from 'hooks/useLocalState';
import Icon from 'icons/Icon';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import assert from 'utils/assert';
import clickOnEnterOrSpace from 'utils/clickOnEnterOrSpace';
import { useTheme } from 'theme/Theme';
import { getDefaultValues } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
  input: {
    '& input, textarea': {
      fontSize: '0.8rem',
    },
  },
  tip: {
    marginLeft: 8,
    marginBottom: 8,
  },
  endAdornment: {
    right: 96,
  },
  inputRoot: {
    paddingRight: 0,
  },
});

const useOptionStyles = makeStyles((theme: Theme) => ({
  inner: {
    marginBottom: 8,
    display: 'flex',
    width: 88,
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    position: 'relative',
  },
  option: {
    display: 'flex',
    alignSelf: 'center',
    border: '1px solid transparent',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    flex: 1,
    height: 32,
    outline: 'none',
    position: 'relative',
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.2s',
    '& > div': {
      alignSelf: 'center',
    },
    '&:not(:last-child)::after': {
      content: '""',
      position: 'absolute',
      top: 8,
      bottom: 8,
      right: 0,
      width: 1,
      backgroundColor: theme.palette.divider,
    },
    '&:not(:first-of-type)': {
      marginLeft: '-1px',
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  selected: {
    color: theme.palette.primary.main,
  },
  s: {
    backgroundColor: theme.palette.background.paper,
    position: 'absolute',
    top: 4,
    bottom: 4,
    borderRadius: 4,
    margin: '0 4px',
    boxShadow: 'rgb(0 0 0 / 25%) 0px 1px 6px 0px',
    transition: 'left 0.2s cubic-bezier(0.2,0,0,1)',
  },
}));

function Option({
  icon,
  selected,
  onSelect,
  tip,
  testid,
}: {
  icon: React.ReactElement;
  selected?: boolean;
  onSelect: () => void;
  tip: string;
  testid: string;
}) {
  const classes = useOptionStyles();

  return (
    <div
      data-testid={testid}
      data-tip={tip}
      onKeyDown={(e) => clickOnEnterOrSpace(e, onSelect)}
      role="button"
      tabIndex={0}
      onMouseDown={onSelect}
      onTouchStart={onSelect}
      className={clsx(classes.option, selected ? classes.selected : null)}
      aria-label={tip}
    >
      {icon}
    </div>
  );
}

function ColorExpressionProperty({
  activeItemId,
  def,
  mgr,
  pageId,
  property,
  onChange,
  appUid,
  value: text2 = getDefaultValues<ExpressionValue>(mgr, property),
  isViewOnlyUser = false,
}: PropertyEditorProps<ColorExpressionProp, ExpressionValue>) {
  const classes = useStyles();
  const [undoable, setUndoable] = useState(true);
  const optionClasses = useOptionStyles();
  const text: ExpressionValue = useMemo(() => {
    const x: ExpressionValue =
      typeof text2 === 'string' ? { value: text2, type: 'text' } : text2;
    if (x.value === null) {
      return { type: 'text', value: '' };
    }
    return x;
  }, [text2]);
  assert(
    typeof text.value === 'string',
    `text.value must be string instead found ${typeof text.value} ${JSON.stringify(
      text,
    )}`,
  );
  const [localValue, setLocalValue] = useLocalState(text.value, (val) => {
    onChange(property, { type: text.type, value: val }, undoable);
    setUndoable(false);
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const setExpressionProps = useStateXAtomSetter(expressionPropsAtom, {
    params: { appUid, pageId, itemId: activeItemId },
  });
  const ref = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  // do not use the designer path, instead use some transient path
  // as this could be used in UpdateProp Action in which case we need to
  // call onChange to change the property and not update the designer path directly
  const path = [
    'transient',
    '_expression_',
    appUid,
    pageId,
    activeItemId,
    property,
  ];
  const [expressionValue, setExpressionValue] = useStateX<
    ExpressionValue | undefined
  >(path, undefined);

  useEffect(() => {
    // reset the value in the trasient path
    setExpressionValue(undefined);
    return () => {
      setExpressionValue(undefined);
    };
  }, [setExpressionValue]);

  useEffect(() => {
    if (expressionValue) {
      // user have selected some item in the popup
      assert(
        typeof expressionValue.value === 'string',
        'expressionValue.value must be string',
      );
      setLocalValue(expressionValue.value);
      inputRef.current?.focus();
    }
  }, [expressionValue, setLocalValue]);

  const openExpressionBuilder = (val: string) => {
    assert(typeof val === 'string', 'val must be a string');
    setExpressionProps({
      path,
      returnType: 'string',
      open: true,
      type: 'expressionObject',
      value: { type: 'expression', value: val },
      context: {
        appUid,
        pageId,
        itemId: activeItemId,
        property,
        data: {},
      },
    });
  };

  const endAdornment = (
    <InputAdornment position="end">
      <div className={optionClasses.inner} ref={ref}>
        <Box
          sx={{
            width: 'calc(50% - 8px)',
            left: text.type === 'text' ? 0 : '50%',
          }}
          className={optionClasses.s}
        />
        <Option
          testid="text"
          icon={
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor:
                  text.type === 'text'
                    ? text.value || 'primary.main'
                    : 'primary.main',
                borderRadius: '50%',
                pointerEvents: 'none',
              }}
            />
          }
          onSelect={() => {
            if (text.type !== 'text') {
              setLocalValue('');
              onChange(property, { value: '', type: 'text' });
            }
            setAnchorEl(ref.current);
            setExpressionValue(undefined);
          }}
          selected={text.type === 'text'}
          tip="Color"
        />
        <Option
          testid="expression"
          icon={<Icon disabled={isViewOnlyUser} iconName="sigma" />}
          onSelect={
            isViewOnlyUser
              ? () => {}
              : () => {
                  let val = localValue;
                  if (text.type !== 'expression') {
                    val = '';
                    setLocalValue(val);
                    onChange(property, {
                      value: val,
                      type: 'expression',
                    });
                  }
                  setExpressionValue(undefined);
                  openExpressionBuilder(val as string);
                }
          }
          selected={text.type === 'expression'}
          tip={
            text.type === 'expression'
              ? 'Open Expression Builder'
              : 'Expression'
          }
        />
      </div>
      {helpText ? (
        <Icon
          className={classes.tip}
          data-tip-at="top"
          data-tip-width={600}
          data-tip-delay={0}
          data-tip={helpText}
          iconName="comment-exclamation"
        />
      ) : null}
    </InputAdornment>
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <FormControl
        disabled={isViewOnlyUser}
        className={classes.root}
        classes={{ root: classes.input }}
        fullWidth
        variant="standard"
        data-testid={property}
      >
        <InputLabel>{def.label}</InputLabel>
        <Input
          disabled={isViewOnlyUser}
          data-testid={`${property}-input`}
          spellCheck={false}
          autoCorrect="off"
          fullWidth
          placeholder={
            text.type === 'text'
              ? def.placeholder ?? 'e.g. primary.main or #fff'
              : 'e.g. getColor($.ds.attr)'
          }
          onFocus={(e) => {
            e.target.select();
            setUndoable(true);
          }}
          onBlur={() => {
            setUndoable(true);
          }}
          type="text"
          value={localValue ?? ''}
          onChange={(e) => {
            setLocalValue(e.target.value);
            setExpressionValue(undefined);
          }}
          inputRef={inputRef}
          endAdornment={endAdornment}
        />
      </FormControl>
      {!isViewOnlyUser ? (
        <ColorPickerPopover
          anchorEl={anchorEl}
          onClose={handleClose}
          onChange={(colorString) => {
            setLocalValue(colorString);
            onChange(property, { ...text, value: colorString }, undoable);
            setUndoable(false);
          }}
          value={text.value}
        />
      ) : null}
    </>
  );
}

export function ColorPickerPopover({
  anchorEl,
  value,
  onChange,
  onClose,
}: {
  anchorEl: HTMLDivElement | null;
  value: string;
  onChange: (val: string) => void;
  onClose: () => void;
}) {
  const theme = useTheme();

  return anchorEl ? (
    <Popover
      id="color"
      open
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <ColorPicker
        value={value || theme.palette.primary.main}
        onChange={(c) => {
          const colorString =
            typeof c === 'object' ? `rgba(${c.r},${c.g},${c.b},${c.a})` : c;
          onChange(colorString);
        }}
      />
    </Popover>
  ) : null;
}

export default memo(ColorExpressionProperty);
